import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

export default function Register() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "athlete" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ ok: true, error: null });
  const invitedEmail = params.get("email") || "";

  // Pre-fill email + role from invite link (?email=…&role=athlete)
  useEffect(() => {
    const emailParam = params.get("email");
    const roleParam = params.get("role");
    setForm((f) => ({
      ...f,
      email: emailParam ? emailParam.toLowerCase() : f.email,
      role: roleParam && ["athlete", "coach"].includes(roleParam) ? roleParam : f.role,
    }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      // Anonymous POST — no token issued, no auto-login.
      // Account is created in `pending` state until an admin approves it.
      const { data } = await axios.post(`${API}/auth/register`, form);
      setEmailStatus(data?.email_status || { ok: true, error: null });
      setSubmitted(true);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="register-pending-screen">
        <div className="w-full max-w-md text-center">
          <CheckCircle2 className="w-16 h-16 text-[#34C759] mx-auto mb-6" />
          <div className="overline mb-2 text-[#34C759]">// registration received</div>
          <h2 className="font-heading text-4xl font-black uppercase mb-4">Pending Approval</h2>
          <p className="text-[#A1A1AA] text-sm mb-3">
            Thanks <span className="text-white font-bold">{form.name}</span> — your registration has been submitted successfully.
          </p>

          <div className="border border-white/10 rounded-lg p-4 mb-6 text-left bg-[#121212]" data-testid="next-steps-card">
            <div className="overline text-[10px] mb-3 text-[#A78BFA]">// what happens next</div>
            <ol className="text-sm text-[#E5E5E5] space-y-2.5 list-decimal pl-4">
              <li>
                {emailStatus.ok ? (
                  <>
                    <span className="text-white font-semibold">Confirmation email sent</span> to
                    <span className="text-[#A78BFA] ml-1">{form.email}</span>.
                    Check your inbox (and spam folder) for the receipt.
                  </>
                ) : (
                  <>
                    <span className="text-[#FF3B30] font-bold">Confirmation email FAILED to send</span> to
                    <span className="text-[#FF3B30] ml-1">{form.email}</span>.
                    Your account was still created, but no email went out — see the red banner below.
                  </>
                )}
              </li>
              <li>
                <span className="text-white font-semibold">An administrator will review</span> your request.
                You&apos;ll receive a second email the moment your account is approved.
              </li>
              <li>
                <span className="text-white font-semibold">Then you can log in</span> with the
                email + password you just chose.
              </li>
            </ol>
          </div>

          {!emailStatus.ok && (
            <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/10 rounded-lg p-4 mb-6 text-left" data-testid="register-email-failed-banner">
              <div className="overline text-[#FF3B30] mb-2">// email delivery failed</div>
              <p className="text-sm text-[#FCA5A5] leading-relaxed">
                The system was unable to send your confirmation email. Your account was still created in
                <b className="text-white"> Pending Approval</b> status, so an administrator can still
                review and activate it.
              </p>
              {emailStatus.error && (
                <pre className="mt-2 text-[10px] font-mono text-[#FCA5A5] whitespace-pre-wrap break-words bg-black/30 p-2 rounded">{emailStatus.error}</pre>
              )}
              <p className="text-[11px] text-[#FCA5A5] mt-2">
                If you need confirmation that your account exists, please contact your team
                administrator directly.
              </p>
            </div>
          )}

          {emailStatus.ok && (
            <div className="text-[11px] text-[#71717A] mb-6 leading-relaxed">
              Didn&apos;t get the email within a few minutes? It may be in your spam folder. If you still don&apos;t see it after an hour, contact your team administrator.
            </div>
          )}

          <Link to="/login" data-testid="back-to-login-link"
                className="inline-block px-6 py-3 border border-white/20 hover:border-white/40 font-heading text-xs uppercase tracking-[0.25em]">
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
        <div className="overline mb-2">// New Operator</div>
        <h2 className="font-heading text-4xl font-black uppercase mb-2">Create Account</h2>
        {invitedEmail && (
          <div className="mb-4 px-3 py-2 border border-[#34C759]/40 bg-[#34C759]/10 text-[#34C759] text-xs" data-testid="invite-banner">
            ✓ You were invited by your coach — your email is pre-filled below.
          </div>
        )}
        <p className="text-[#71717A] text-xs mb-8">Accounts require administrator approval before first login.</p>
        {err && <div className="mb-4 px-3 py-2 border border-[#FF3B30]/40 text-[#FF3B30] text-sm" data-testid="register-error">{err}</div>}

        {["name","email","password"].map((f) => (
          <label key={f} className="block mb-5">
            <span className="overline block mb-2">{f}</span>
            <input
              data-testid={`register-${f}`}
              type={f === "password" ? "password" : f === "email" ? "email" : "text"}
              autoComplete={f === "password" ? "new-password" : f === "email" ? "email" : "name"}
              inputMode={f === "email" ? "email" : undefined}
              autoCapitalize={f === "name" ? "words" : "off"}
              value={form[f]}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              required
              minLength={f === "password" ? 8 : undefined}
              className="w-full bg-transparent border-b border-white/20 py-3 text-base text-white focus:border-[#FF3B30]"
            />
          </label>
        ))}

        <label className="block mb-8">
          <span className="overline block mb-2">Role</span>
          <select
            data-testid="register-role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full bg-[#121212] border border-white/10 py-3 px-3 text-base text-white focus:border-[#FF3B30] rounded"
          >
            <option value="athlete">Athlete</option>
            <option value="coach">Coach</option>
          </select>
        </label>

        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit"
          className="w-full bg-[#FF3B30] hover:bg-[#D92B22] active:bg-[#B91C16] disabled:opacity-50 text-white font-bold uppercase tracking-[0.18em] py-4 min-h-[48px] text-sm transition-colors rounded"
        >
          {busy ? "Submitting…" : "Request Account"}
        </button>
        <div className="mt-6 text-xs text-[#A1A1AA]">
          Have an account? <Link to="/login" className="text-white underline">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
